import { useState, useEffect, useCallback } from 'react';
import { fetchQuery } from 'react-relay';
import graphql from 'babel-plugin-relay/macro';
import { relayEnvironment } from '../../../../relay';
import type { useWalletTransactionRelayByIdQuery } from './__generated__/useWalletTransactionRelayByIdQuery.graphql';

// ============================================================================
// GraphQL Definitions
// ============================================================================

const WalletTransactionByIdQuery = graphql`
  query useWalletTransactionRelayByIdQuery($id: ID!) {
    getWalletTransaction(id: $id) {
      id
      oldId
      amount
      amountRefunded
      chargeTotal
      createdOn
      description
      refundDescription
      refunded
      taxAmount
      taxRate
      type
      user {
        id
        firstName
        lastName
      }
      rental {
        id
      }
    }
  }
`;

// ============================================================================
// Types
// ============================================================================

export interface WalletTransactionGraphQL {
  id: string;
  oldId?: string | null;
  amount?: number | null;
  amountRefunded?: number | null;
  chargeTotal?: number | null;
  createdOn?: string | null;
  description?: string | null;
  refundDescription?: string | null;
  refunded?: boolean | null;
  taxAmount?: any | null;
  taxRate?: any | null;
  type?: string | null;
  user?: { id: string; firstName?: string | null; lastName?: string | null } | null;
  rental?: { id: string } | null;
}

// ============================================================================
// Hook
// ============================================================================

export function useWalletTransactionByIdQuery(
  walletTransactionId: string | undefined,
  options?: { enabled?: boolean }
) {
  const [data, setData] = useState<WalletTransactionGraphQL | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isFetched, setIsFetched] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [fetchCount, setFetchCount] = useState(0);

  const enabled = options?.enabled !== false && Boolean(walletTransactionId);

  useEffect(() => {
    if (!enabled || !walletTransactionId) {
      setIsLoading(false);
      return;
    }

    let cancelled = false;

    const doFetch = async () => {
      try {
        setIsLoading(true);

        const result = await fetchQuery<useWalletTransactionRelayByIdQuery>(
          relayEnvironment,
          WalletTransactionByIdQuery,
          { id: walletTransactionId }
        ).toPromise();

        if (cancelled) return;

        if (result) {
          setData(result.getWalletTransaction as WalletTransactionGraphQL | null);
          setError(null);
        }
      } catch (err) {
        if (cancelled) return;
        setError(
          err instanceof Error ? err : new Error('Failed to fetch wallet transaction')
        );
      } finally {
        if (!cancelled) {
          setIsLoading(false);
          setIsFetched(true);
        }
      }
    };

    doFetch();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, walletTransactionId, fetchCount]);

  const refetch = useCallback(() => {
    setFetchCount((c) => c + 1);
  }, []);

  return { data, isLoading, isFetched, error, refetch };
}
